import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ExternalLink, FolderSync, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceField, WorkspaceInput, WorkspaceSelect } from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import { DOCUMENT_PARTY_LABEL } from '@/lib/api/documents';
import {
  DOCUMENT_SOURCE_PARTIES,
  describeDocumentSource,
  isDocumentSourceLocked,
  useCanManageDocumentSources,
  useConnectDocumentSourceMutation,
  useDisconnectDocumentSourceMutation,
  useDocumentSourceItemsQuery,
  useDocumentSourceQuery,
  useSyncNowMutation,
  type DocumentSourceDto,
  type DocumentSourceItem,
  type DocumentSourceParty,
} from '@/lib/api/document-sources';
import { cn } from '@/lib/utils/cn';
import { formatRelative } from '@/lib/utils/format';

/**
 * The connected SharePoint folder, under the folder tree in the Documents
 * tab's left rail (E18-F01 / T-0211; E18 design note §5).
 *
 * Not connected: a lead sees a quiet "Connect SharePoint folder", which opens
 * an inline form — the folder URL and who the documents in it are from
 * (Vendor · Avero · Client, the document party of E19 / T-0215). Connect
 * is one call: the API resolves the URL and connects. A consultant sees
 * nothing. Connected: the site and path as a link to SharePoint, when the
 * folder was last read with the counts, and a status line — not yet synced,
 * reading, access lost (critical) or the last error (quiet). "N skipped" and
 * "N removed in SharePoint" open the lists from the items route. Leads get
 * Sync now and Disconnect, which confirms in place; documents already taken
 * stay after a disconnect. Errors from the API are shown in place.
 *
 * One direction: rail → grid → document. No modal, no second screen, and
 * nothing here is AI. Tokens from the Tailwind theme only.
 */

export interface ConnectedFolderCardProps {
  engagementId: string;
  className?: string;
}

export function ConnectedFolderCard({ engagementId, className }: ConnectedFolderCardProps) {
  const canManage = useCanManageDocumentSources();
  const query = useDocumentSourceQuery(engagementId);
  const source = query.data ?? null;

  // Nothing until the folder is known (no flash of "Connect" over a connected
  // folder), and nothing, quietly, on a server that has no route yet.
  if (query.isLoading) return null;
  if (query.error instanceof ApiError && query.error.status === 404) return null;
  if (!source && !canManage && !query.error) return null;

  return (
    <>
      <hr className="border-t border-ink-100" />
      <div className={className}>
        <div className="t-h3 mb-3 font-semibold text-ink-900">Connected folder</div>
        {query.error ? (
          <ErrorMessage error={query.error} />
        ) : source ? (
          <SourceCard engagementId={engagementId} source={source} canManage={canManage} />
        ) : (
          <ConnectForm engagementId={engagementId} />
        )}
      </div>
    </>
  );
}

/** "Connect SharePoint folder" and, once opened, the form. */
function ConnectForm({ engagementId }: { engagementId: string }) {
  const [open, setOpen] = useState(false);
  const [folderUrl, setFolderUrl] = useState('');
  const [party, setParty] = useState<DocumentSourceParty>('vendor');
  const connect = useConnectDocumentSourceMutation(engagementId);

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          connect.reset();
          setOpen(true);
        }}
      >
        <FolderSync className="h-4 w-4" aria-hidden="true" />
        <span>Connect SharePoint folder</span>
      </Button>
    );
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate(
          { folder_url: folderUrl.trim(), source_party: party },
          {
            onSuccess: () => {
              setOpen(false);
              setFolderUrl('');
              setParty('vendor');
            },
          },
        );
      }}
    >
      <WorkspaceField label="Folder URL" help="Copied from SharePoint's address bar or Copy link.">
        {(id, describedBy) => (
          <WorkspaceInput
            id={id}
            type="url"
            required
            autoFocus
            value={folderUrl}
            onChange={(e) => setFolderUrl(e.target.value)}
            placeholder="https://…sharepoint.com/sites/…"
            aria-describedby={describedBy}
            className="text-body-sm"
          />
        )}
      </WorkspaceField>
      <WorkspaceField label="Documents in this folder are from">
        {(id) => (
          <WorkspaceSelect
            id={id}
            value={party}
            onChange={(e) => setParty(e.target.value as DocumentSourceParty)}
            className="text-body-sm"
          >
            {DOCUMENT_SOURCE_PARTIES.map((p) => (
              <option key={p} value={p}>
                {DOCUMENT_PARTY_LABEL[p]}
              </option>
            ))}
          </WorkspaceSelect>
        )}
      </WorkspaceField>
      {connect.error ? <ErrorMessage error={connect.error} /> : null}
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={connect.isPending || folderUrl.trim().length === 0}
          className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
        >
          {connect.isPending ? <Spinner /> : null}
          <span>{connect.isPending ? 'Connecting…' : 'Connect'}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={connect.isPending}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The connected folder: link, counts, status, lists and the lead's controls. */
function SourceCard({
  engagementId,
  source,
  canManage,
}: {
  engagementId: string;
  source: DocumentSourceDto;
  canManage: boolean;
}) {
  const [showSkipped, setShowSkipped] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const syncNow = useSyncNowMutation(engagementId);
  const disconnect = useDisconnectDocumentSourceMutation(engagementId);

  const lastSyncAt = source.last_sync_at ?? null;
  // The skipped list is fetched when opened; the removed list always, since
  // the DTO carries no removed count.
  const skipped = useDocumentSourceItemsQuery(engagementId, source.id, 'skipped', {
    enabled: showSkipped,
    syncedAt: lastSyncAt,
  });
  const removed = useDocumentSourceItemsQuery(engagementId, source.id, 'removed', {
    syncedAt: lastSyncAt,
  });
  const syncedCount = source.synced_count ?? 0;
  const skippedCount = source.skipped_count ?? 0;
  const removedCount = removed.data?.length ?? 0;
  const status = describeDocumentSource(source);
  const locked = isDocumentSourceLocked(source);
  const error = syncNow.error ?? disconnect.error;

  // "Site · path", unless the path already starts with the site name.
  const siteName = source.site_name ?? '';
  const pathLabel =
    siteName && !source.display_path.startsWith(siteName)
      ? `${siteName} · ${source.display_path}`
      : source.display_path;

  return (
    <div className="flex flex-col gap-2">
      <a
        href={source.web_url}
        target="_blank"
        rel="noreferrer"
        className={cn(
          'inline-flex items-start gap-1.5 text-body-sm font-medium text-ink-900 no-underline hover:underline',
          'focus-visible:rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        )}
      >
        <span className="min-w-0 break-words">{pathLabel}</span>
        <ExternalLink className="mt-1 h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
      </a>

      {lastSyncAt ? (
        <div className="text-meta text-ink-500">
          Synced {formatRelative(lastSyncAt)} · {syncedCount} {syncedCount === 1 ? 'file' : 'files'}
          {skippedCount > 0 ? (
            <>
              {' · '}
              <ListToggle expanded={showSkipped} onClick={() => setShowSkipped((v) => !v)}>
                {skippedCount} skipped
              </ListToggle>
            </>
          ) : null}
          {removedCount > 0 ? (
            <>
              {' · '}
              <ListToggle expanded={showRemoved} onClick={() => setShowRemoved((v) => !v)}>
                {removedCount} removed in SharePoint
              </ListToggle>
            </>
          ) : null}
        </div>
      ) : null}

      {status ? (
        <div
          role={status.tone === 'critical' ? 'alert' : undefined}
          className={cn(
            'flex items-center gap-1.5 text-meta',
            status.tone === 'critical' ? 'font-medium text-posture-critical' : 'text-ink-500',
          )}
        >
          {status.tone === 'busy' ? <Spinner /> : null}
          <span>{status.message}</span>
        </div>
      ) : null}

      {showSkipped ? <ItemList query={skipped} emptyText="Nothing skipped." /> : null}
      {showRemoved ? <ItemList query={removed} emptyText="Nothing removed." /> : null}

      {error ? <ErrorMessage error={error} /> : null}

      {canManage ? (
        confirmingDisconnect ? (
          <div
            role="group"
            aria-label="Disconnect this folder"
            className="flex flex-col gap-2 rounded-card border border-ink-100 bg-paper-50 p-2.5 text-meta text-ink-700"
          >
            <span>Stop syncing this folder? Documents already taken stay.</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disconnect.isPending}
                onClick={() =>
                  disconnect.mutate(source.id, { onSettled: () => setConfirmingDisconnect(false) })
                }
                className="border-posture-critical text-posture-critical"
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Confirm disconnect'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disconnect.isPending}
                onClick={() => setConfirmingDisconnect(false)}
              >
                Keep
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={locked || syncNow.isPending}
              onClick={() => syncNow.mutate(source.id)}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              <span>{syncNow.isPending ? 'Requesting…' : 'Sync now'}</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                syncNow.reset();
                disconnect.reset();
                setConfirmingDisconnect(true);
              }}
            >
              Disconnect
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}

/** "2 skipped" / "1 removed in SharePoint": opens the list under the line. */
function ListToggle({
  expanded,
  onClick,
  children,
}: {
  expanded: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onClick}
      className={cn(
        'rounded-chip underline underline-offset-[3px] hover:text-ink-900',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
      )}
    >
      {children}
    </button>
  );
}

/** The entries in one state: name, and the reason when there is one. */
function ItemList({
  query,
  emptyText,
}: {
  query: UseQueryResult<DocumentSourceItem[]>;
  emptyText: string;
}) {
  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading…
      </div>
    );
  }
  if (query.error) return <ErrorMessage error={query.error} />;
  const items = query.data ?? [];
  if (items.length === 0) return <div className="text-meta text-ink-500">{emptyText}</div>;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map((item) => (
        <li key={item.id} className="rounded-card bg-paper-50 px-2.5 py-1.5">
          <div
            className="truncate text-meta font-medium text-ink-900"
            title={item.path ?? item.name}
          >
            {item.name}
          </div>
          {item.reason ? <div className="text-meta text-ink-500">{item.reason}</div> : null}
        </li>
      ))}
    </ul>
  );
}
